import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { LightDarkGreyBox } from "../../styled";
import CustomConfirmationDialog from "../../dialogs/CustomConfirmationDialog";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import DeviceService from "../../../api/deviceService";
import RegisterDeviceDialog from "./RegisterDeviceDialog";
import DeviceDetailsDialog from "./DeviceDetailsDialog";

const DeviceManager = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [motherUserInfo, setMotherUserInfo] = useState(null);
  const [error, setError] = useState(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deviceToRevoke, setDeviceToRevoke] = useState(null);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await DeviceService.getMyDevices();

      if (response?.data) {
        setDevices(response.data.devices || []);
        setMotherUserInfo({
          username: response.data.mother_user,
          userId: response.data.mother_user_id,
          userType: response.data.user_type,
        });
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
      setError("Erro ao buscar dispositivos. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSuccess = (newDevice) => {
    setDevices((prevDevices) => [...prevDevices, newDevice]);
    setRegisterDialogOpen(false);
    dispatchSnackBarMessage(
      Date.now(),
      "Dispositivo registrado com sucesso",
      "success"
    );
  };

  const handleViewDetails = (device) => {
    setSelectedDevice(device);
    setDetailsDialogOpen(true);
  };

  const handleRevokeDevice = async () => {
    if (!deviceToRevoke) {
      return;
    }

    try {
      setLoading(true);
      await DeviceService.revokeDevice(deviceToRevoke.id);

      setDevices((prevDevices) =>
        prevDevices.map((device) =>
          device.id === deviceToRevoke.id
            ? { ...device, is_active: false }
            : device
        )
      );

      dispatchSnackBarMessage(
        Date.now(),
        "Dispositivo revogado com sucesso",
        "success"
      );

      setRevokeDialogOpen(false);
      setDeviceToRevoke(null);
    } catch (err) {
      console.error("Error revoking device:", err);
      dispatchSnackBarMessage(
        Date.now(),
        "Erro ao revogar dispositivo",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async () => {
    if (!deviceToDelete) {
      return;
    }

    try {
      setLoading(true);
      await DeviceService.deleteDevice(deviceToDelete.id);

      setDevices((prevDevices) =>
        prevDevices.filter((device) => device.id !== deviceToDelete.id)
      );

      dispatchSnackBarMessage(
        Date.now(),
        "Dispositivo excluído com sucesso",
        "success"
      );

      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch (err) {
      console.error("Error deleting device:", err);
      dispatchSnackBarMessage(
        Date.now(),
        "Erro ao excluir dispositivo",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTokenRegenerated = (updatedDevice) => {
    setDevices((prevDevices) =>
      prevDevices.map((device) =>
        device.id === updatedDevice.id ? updatedDevice : device
      )
    );

    dispatchSnackBarMessage(
      Date.now(),
      "Token regenerado com sucesso",
      "success"
    );
  };

  const openRevokeDialog = (device) => {
    setDeviceToRevoke(device);
    setRevokeDialogOpen(true);
  };

  const openDeleteDialog = (device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "Nunca usado";
    }

    return new Date(dateString).toLocaleString("pt-BR");
  };

  const getStatusChip = (isActive) => {
    if (isActive) {
      return <Chip label="Ativo" color="success" size="small" />;
    }

    return <Chip label="Revogado" color="error" size="small" />;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <LightDarkGreyBox sx={{ mb: 2, display: "block" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            Dispositivos Registrados
            {motherUserInfo && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Proprietário: {motherUserInfo.username} -{" "}
                {motherUserInfo.userType})
              </Typography>
            )}
          </Typography>
          <Box>
            <Tooltip title="Atualizar">
              <IconButton
                onClick={fetchDevices}
                size="small"
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRegisterDialogOpen(true)}
              disabled={loading}
              sx={{ ml: 1 }}
            >
              Registrar Dispositivo
            </Button>
          </Box>
        </Box>
      </LightDarkGreyBox>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && devices.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      ) : devices.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            px: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" gutterBottom>
            Nenhum dispositivo encontrado.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Registre um dispositivo para permitir que aplicativos se conectem à
            sua conta.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setRegisterDialogOpen(true)}
            disabled={loading}
          >
            Registrar Primeiro Dispositivo
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Identificador</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registrado por</TableCell>
                <TableCell>Último uso</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {device.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {device.device_identifier}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(device.is_active)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {device.registered_by_username || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(device.last_used_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalhes">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(device)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {device.is_active && (
                      <Tooltip title="Revogar">
                        <IconButton
                          size="small"
                          onClick={() => openRevokeDialog(device)}
                          disabled={loading}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(device)}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        Estes tokens são usados para autenticar aplicativos de dispositivos.
        Cada dispositivo deve ter seu próprio token único. Não compartilhe estes
        tokens com ninguém.
      </Alert>

      <RegisterDeviceDialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
        onSuccess={handleRegisterSuccess}
      />

      <DeviceDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        device={selectedDevice}
        onTokenRegenerated={handleTokenRegenerated}
      />

      <CustomConfirmationDialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        onConfirm={handleRevokeDevice}
        title="Revogar Dispositivo"
        dialogContent={`Tem certeza que deseja revogar o dispositivo "${deviceToRevoke?.name}"? O dispositivo não poderá mais se conectar até que um novo token seja gerado.`}
        confirmText="Revogar"
        cancelText="Cancelar"
      />

      <CustomConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteDevice}
        title="Excluir Dispositivo"
        dialogContent={`Tem certeza que deseja excluir o dispositivo "${deviceToDelete?.name}"? Esta ação não pode ser desfeita e o dispositivo precisará ser registrado novamente.`}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </Box>
  );
};

export default DeviceManager;
